import { KeyRoundIcon, MailIcon } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'

import { DesktopNavigation, MobileNavigation } from '../components/Navigation'
import {
  PreferenceButton,
  PreferenceCategory,
  PreferenceInfo,
  PreferenceInput,
  PreferenceLink,
  PreferenceScreen,
  PreferenceSubmit,
} from '../components/Preference'
import {
  authClient,
  fetchMe,
  postSignInPath,
  promptGoogleOneTap,
  signInWithGoogle,
  DEFAULT_APP_PATH,
  useConfig,
  useMe,
} from '../lib/auth-client'

/** Email + password sign-in. */
export default function Login() {
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const next = params.get('next') || DEFAULT_APP_PATH
  const { data: me, isLoading, mutate } = useMe()
  const { data: config } = useConfig()
  const googleEnabled = config?.googleEnabled ?? false

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    if (!me) return
    if (!me.user || me.user.isAnonymous) {
      if (!me.user) void promptGoogleOneTap(next)
      return
    }
    navigate(postSignInPath(me, next), { replace: true })
  }, [me, navigate, next])

  async function signIn() {
    setBusy(true)
    try {
      const { error: err } = await authClient.signIn.email({
        email: email.trim(),
        password,
        rememberMe: true,
      })
      if (err) {
        toast.error(err.message || 'Inloggen mislukt')
        return
      }
      await mutate()
      navigate(postSignInPath(await fetchMe(), next), { replace: true })
    } catch (err) {
      toast.error(err instanceof Error ? err.message : String(err))
    } finally {
      setBusy(false)
    }
  }

  async function forgotPassword() {
    if (!email.trim()) {
      toast.error('Vul eerst je e-mailadres in.')
      return
    }
    setBusy(true)
    try {
      const { error: err } = await authClient.requestPasswordReset({
        email: email.trim(),
        redirectTo: `${window.location.origin}/reset-password`,
      })
      if (err) {
        toast.error(err.message || 'Resetlink versturen mislukt')
        return
      }
      toast.success('Check je inbox', {
        description: 'We stuurden een resetlink naar je e-mail.',
      })
    } finally {
      setBusy(false)
    }
  }

  const checking = isLoading || !me || (me.user != null && !me.user.isAnonymous)

  return (
    <>
      <main className="bg-green-950 text-white min-h-screen">
        <DesktopNavigation />
        <div className="p-4 lg:p-6 max-w-xl mx-auto pb-28 md:pb-6">
          {checking ? (
            <PreferenceInfo title="Laden…" />
          ) : (
            <PreferenceScreen title="Inloggen" backHref="/">
              {googleEnabled ? (
                <PreferenceCategory filled>
                  <PreferenceButton
                    icon={<GoogleIcon className="h-6 w-6" />}
                    title="Inloggen met Google"
                    onPress={() => void signInWithGoogle(next)}
                    disabled={busy}
                  />
                </PreferenceCategory>
              ) : null}

              <PreferenceCategory filled title="Met wachtwoord">
                <PreferenceInput
                  icon={<MailIcon className="h-6 w-6" />}
                  title="E-mailadres"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={setEmail}
                  placeholder="jij@voorbeeld.be"
                  autoFocus={!googleEnabled}
                />
                <PreferenceInput
                  icon={<KeyRoundIcon className="h-6 w-6" />}
                  title="Wachtwoord"
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={setPassword}
                  placeholder="••••••••"
                  minLength={8}
                />
                <PreferenceSubmit
                  title={busy ? 'Bezig…' : 'Inloggen'}
                  onPress={signIn}
                  disabled={busy || !email.trim() || !password}
                />
              </PreferenceCategory>

              <PreferenceCategory filled>
                <PreferenceButton
                  title="Wachtwoord vergeten?"
                  summary="Stuur een resetlink"
                  onPress={forgotPassword}
                  disabled={busy}
                />
                <PreferenceLink
                  icon={<MailIcon className="h-6 w-6" />}
                  title="Liever een inloglink?"
                  href={`/login/email?next=${encodeURIComponent(next)}`}
                />
              </PreferenceCategory>
            </PreferenceScreen>
          )}
        </div>
      </main>
      <MobileNavigation />
    </>
  )
}

function GoogleIcon({ className }: { className?: string }) {
  return (
    <svg
      role="img"
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      fill="currentColor"
      aria-hidden
    >
      <title>Google</title>
      <path d="M12.48 10.92v3.28h7.84c-.24 1.84-.853 3.187-1.787 4.133-1.147 1.147-2.933 2.4-6.053 2.4-4.827 0-8.6-3.893-8.6-8.72s3.773-8.72 8.6-8.72c2.6 0 4.507 1.027 5.907 2.347l2.307-2.307C18.747 1.44 16.133 0 12.48 0 5.867 0 .307 5.387.307 12s5.56 12 12.173 12c3.573 0 6.267-1.173 8.373-3.36 2.16-2.16 2.84-5.213 2.84-7.667 0-.76-.053-1.467-.173-2.053H12.48z" />
    </svg>
  )
}
